import { useMemo } from 'react'
import { useSelectFilter } from './fundamentals'
import { useToast } from '@core/toast'
import { useGetUserCategories } from '@core/react-query/features/analytics/hooks/useGetUserCategories'

// --------------------------------------------
// Constants & Types
// --------------------------------------------

export const FALLBACK_USER_CATEGORIES = {
  'Monthly Active - Contributor': '#2E8B57', // Sea Green - Best engagement
  'Monthly Active - Observer': '#FFD700', // Gold - Declining engagement
  'Monthly Inactive (no activity in the last 30 days)': '#DC143C', // Crimson - Worst engagement
} as const

export type UserCategory = keyof typeof FALLBACK_USER_CATEGORIES

// --------------------------------------------
// Helpers
// --------------------------------------------

/**
 * Get the color associated with a user category
 * @param category The user category
 * @returns The hex color code or undefined if not found
 */
export const getUserCategoryColor = (category: string): string | undefined => {
  return category in FALLBACK_USER_CATEGORIES
    ? FALLBACK_USER_CATEGORIES[category as UserCategory]
    : undefined
}

/**
 * Get all available user categories
 * @param apiCategories Categories from API response (array of strings)
 * @returns Array of user category names
 */
export const getUserCategories = (apiCategories?: string[]): string[] => {
  return apiCategories?.length
    ? apiCategories
    : Object.keys(FALLBACK_USER_CATEGORIES)
}

// --------------------------------------------
// Filter Hook
// --------------------------------------------

export type UserCategoriesFilterHookArguments = {
  label: string
  isPinned?: boolean
  multiple?: boolean
}

export const useUserCategoriesFilter = ({
  label,
  isPinned = false,
  multiple = false,
}: UserCategoriesFilterHookArguments) => {
  const toast = useToast()

  const { data: userCategoriesResponse, isLoading } = useGetUserCategories({
    onError: (error) => toast.error(error.message),
  })

  const categoryOptions = useMemo(
    () => getUserCategories(userCategoriesResponse),
    [userCategoriesResponse],
  )

  return useSelectFilter({
    label,
    options: categoryOptions,
    multiple,
    isPinned,
    valueToLabel: (value) => value,
  })
}
